/**
 * Youth & amateur sports grants directory — /resources/youth-sports-grants.
 *
 * Every entry was confirmed against the organisation's own published pages on
 * GRANTS_LAST_VERIFIED. Never add an amount or deadline the org doesn't publish
 * itself; write "Varies" instead. Re-run `npm run check:content-links` (from a
 * box that can reach the open web) and re-verify quarterly — a dead link or a
 * stale amount on a money page costs exactly the trust this page is meant to earn.
 *
 * Deliberately excluded: college scholarships and tuition aid (education funding
 * is not this product — see docs/06-Go-to-Market.md), for-profit program
 * providers, and invite-only funds a family cannot act on.
 */
export const GRANTS_LAST_VERIFIED = '2026-10-01';

export type GrantCategory = 'fees' | 'sport' | 'elite' | 'adaptive' | 'equipment';

export const GRANT_CATEGORIES: { id: GrantCategory; label: string; blurb: string }[] = [
  { id: 'fees', label: 'Fees & registration', blurb: 'Need-based help with club dues, registration and season costs.' },
  { id: 'sport', label: 'Sport-specific funds', blurb: 'Foundations and governing bodies that back athletes in one sport.' },
  { id: 'elite', label: 'Elite & Olympic pathway', blurb: 'Training and travel support for nationally ranked athletes.' },
  { id: 'adaptive', label: 'Adaptive & para sport', blurb: 'Equipment, coaching and travel grants for athletes with disabilities.' },
  { id: 'equipment', label: 'Equipment programs', blurb: 'Donated or discounted gear — usually requested through a team or program.' },
];

export interface Grant {
  name: string;
  url: string;
  category: GrantCategory;
  /** Who can receive it (age, level, income, sport). */
  forWho: string;
  /** What the money or gear covers. */
  covers: string;
  /** Only what the org publishes; otherwise "Varies". */
  amount: string;
  /** individual = the family applies; organization = a team/club/school applies. */
  applyAs: 'individual' | 'organization' | 'both';
  /** One line on how/when to apply. */
  howToApply: string;
  /** Lower-case sport slugs this grant is relevant to; empty = any sport. */
  sports: string[];
}

export const grants: Grant[] = [
  // ── Fees & registration ─────────────────────────────────────────────────
  {
    name: 'All Kids Play — Youth Sports Grants',
    url: 'https://allkidsplay.org/youth-sports-grants/',
    category: 'fees',
    forWho: 'K–12 kids nationwide. Full grant if household income is under 60% of your state median; partial grant if under the state median.',
    covers: 'Registration, equipment, uniforms and other reasonable costs — recreational, class or travel sports.',
    amount: 'Varies, capped at $3,500 per child per year',
    applyAs: 'individual',
    howToApply: 'Request the application through their contact form at least 30 days before the registration deadline; income documents required. Rolling, while funds last.',
    sports: [],
  },
  {
    name: 'Every Kid Sports — Every Kid Sports Pass',
    url: 'https://pass.everykidsports.org/',
    category: 'fees',
    forWho: 'Ages 4–18 enrolled in Medicaid, SNAP or WIC, playing a recreational (not elite or travel) sport with a season of 4+ weeks.',
    covers: 'Registration fees.',
    amount: 'Up to $150 per child per season',
    applyAs: 'individual',
    howToApply: 'Parents apply online with proof of benefits. Opens in seasonal windows — the Winter 2026 pass opens October 27, 2026.',
    sports: [],
  },
  {
    name: 'T-Mobile Little League Call Up Grant',
    url: 'https://www.littleleague.org/call-up-grant-program/',
    category: 'fees',
    forWho: 'League age 4–16 with proof of SNAP, WIC, Medicaid, free/reduced lunch or foster paperwork, registering with a local Little League.',
    covers: 'Local Little League registration fee.',
    amount: 'Up to $150 per child',
    applyAs: 'individual',
    howToApply: 'One application per child per year. The 2026 season window has closed; watch for next season’s dates.',
    sports: ['baseball', 'softball'],
  },
  // ── Sport-specific funds ────────────────────────────────────────────────
  {
    name: "Women's Sports Foundation — Travel & Training Fund",
    url: 'https://www.womenssportsfoundation.org/wsf_programs/travel-training/',
    category: 'sport',
    forWho: 'Female US citizens or residents, amateur, with an elite competitive record and financial need, on track for national-team level (individuals or teams).',
    covers: 'Coaching, training, equipment and travel toward national and world championships.',
    amount: '$2,500–$10,000 per individual',
    applyAs: 'both',
    howToApply: 'Annual application cycle; the 2026 deadline was June 30. Check the page for the next cycle.',
    sports: [],
  },
  {
    name: 'U.S. Figure Skating Memorial Fund — Competitive Skaters Assistance Program',
    url: 'https://www.usfigureskating.org/memorial-fund',
    category: 'sport',
    forWho: 'U.S. Figure Skating members, juvenile to senior, who meet competitive thresholds (e.g. top 6 at Sectionals) and show financial need.',
    covers: 'Coaching, ice time, equipment, costumes, choreography, off-ice training and PT.',
    amount: 'Varies',
    applyAs: 'individual',
    howToApply: 'Annual window; the 2026 window closed August 31, 2026.',
    sports: ['figure-skating'],
  },
  {
    name: 'USA Fencing — GRACE fee waivers & Fencing the Gap',
    url: 'https://www.usafencing.org/social-impact-outreach-current-programs',
    category: 'sport',
    forWho: 'GRACE: USA Fencing members facing sudden financial hardship. Fencing the Gap: BIPOC fencers, women and girls, and fencers with disabilities.',
    covers: 'GRACE waives entry and event fees at national events; Fencing the Gap gives scholarships and mini-grants.',
    amount: 'Fee waiver (GRACE); varies (Fencing the Gap)',
    applyAs: 'individual',
    howToApply: 'The GRACE link is on each national event page; Fencing the Gap runs an annual cycle.',
    sports: ['fencing'],
  },
  {
    name: 'USTA Foundation — Player Incentive Awards & Excellence Program',
    url: 'https://www.ustafoundation.com/grants_scholarships/player_incentive_awards/',
    category: 'sport',
    forWho: 'Under-resourced junior players aged 10–18 in the USTA Foundation’s NJTL chapter network.',
    covers: 'Coaching, equipment, tournaments and travel.',
    amount: 'Player Incentive Award: $500 one-time; Excellence grants vary',
    applyAs: 'organization',
    howToApply: 'By nomination from your local NJTL chapter, not open application — ask the chapter.',
    sports: ['tennis'],
  },
  {
    name: 'USA Cycling Foundation — Junior development grants',
    url: 'https://usacycling.org/foundation/grants',
    category: 'sport',
    forWho: 'USA Cycling clubs, teams, velodromes and coaches developing junior and U23 riders.',
    covers: 'Junior program and development costs.',
    amount: 'Up to $5,000 per program (2026 Centers of Excellence)',
    applyAs: 'organization',
    howToApply: 'Annual cycle; your club or team applies.',
    sports: ['cycling'],
  },
  {
    name: 'Share Winter Foundation',
    url: 'https://sharewinterfoundation.org/',
    category: 'sport',
    forWho: 'Schools, ski clubs, nonprofit ski areas and rec departments running winter-sports programs for low-income and underrepresented youth.',
    covers: 'Program funding — lessons, lift access, gear.',
    amount: 'Varies; multi-year commitments',
    applyAs: 'organization',
    howToApply: 'Your program applies.',
    sports: ['skiing'],
  },
  // ── Elite & Olympic pathway ─────────────────────────────────────────────
  {
    name: 'USATF Foundation — Elite Athlete Development Grants',
    url: 'https://www.usatffoundation.org/',
    category: 'elite',
    forWho: 'Emerging elite track & field athletes aiming at international competition.',
    covers: 'Travel, coaching, sports medicine and training costs.',
    amount: 'Recent rounds: $8,000–$10,000 per athlete',
    applyAs: 'individual',
    howToApply: 'Competitive annual application (usually spring) judged on performance, financial need and a character reference.',
    sports: ['track-and-field'],
  },
  {
    name: 'USOPC — Athlete grants & funding (Simon Grant and assistance funds)',
    url: 'https://www.usopc.org/grantsandfunding',
    category: 'elite',
    forWho: 'Team USA athletes; the Simon Grant targets high-performing athletes with financial need in contention for LA 2028 or 2030.',
    covers: 'Training and competition costs, plus medical, mental-health and new-parent assistance funds.',
    amount: 'Varies by program',
    applyAs: 'individual',
    howToApply: 'Eligibility runs through your national governing body; check the program pages each year.',
    sports: [],
  },
  // ── Adaptive & para sport ───────────────────────────────────────────────
  {
    name: 'Challenged Athletes Foundation — Annual Grant Program',
    url: 'https://www.challengedathletes.org/grants/',
    category: 'adaptive',
    forWho: 'Athletes of any age or level with a permanent physical disability affecting mobility, neuromuscular function or balance.',
    covers: 'Sport expenses (entry fees, travel, lodging, training), adaptive equipment and sport prostheses.',
    amount: 'Varies; equipment funding requests up to $3,500',
    applyAs: 'individual',
    howToApply: 'Open now: September 28 – November 13, 2026 (5pm PT). Awards notified by March 2027.',
    sports: [],
  },
  {
    name: 'Move United — Tatyana McFadden Competitor Award',
    url: 'https://moveunitedsport.org/tatyana-mcfadden-competitor-award/',
    category: 'adaptive',
    forWho: 'Youth athletes with disabilities aged 4–18 competing in Move United–sanctioned summer events.',
    covers: 'Competition travel.',
    amount: 'Up to $500',
    applyAs: 'individual',
    howToApply: 'Apply through Move United; check the page for the current window.',
    sports: [],
  },
  {
    name: 'Move United — The Hartford Athlete Equipment Grant',
    url: 'https://moveunitedsport.org/athlete-equipment-grant-application/',
    category: 'adaptive',
    forWho: 'Youth and adults with disabilities who show need or emerging-athlete status.',
    covers: 'Adaptive sports equipment.',
    amount: 'Up to $5,000 including shipping',
    applyAs: 'individual',
    howToApply: 'By invitation from Move United or a member organization, usually at a Hartford event — ask your adaptive program.',
    sports: [],
  },
  {
    name: 'Kelly Brush Foundation — Active Fund',
    url: 'https://kellybrushfoundation.org/theactivefund/',
    category: 'adaptive',
    forWho: 'US residents with paralysis from an acquired spinal cord injury.',
    covers: 'Adaptive sports equipment (handcycles, monoskis, sport chairs), paid to the vendor.',
    amount: 'Average about $3,500',
    applyAs: 'individual',
    howToApply: 'Spring and fall cycles each year; vendor quotes and a physician letter required.',
    sports: [],
  },
  {
    name: 'High Fives Foundation — Empowerment Grant',
    url: 'https://highfivesfoundation.org/',
    category: 'adaptive',
    forWho: 'North American residents with a spinal cord injury, brain injury, amputation or other mobility-limiting injury from an outdoor adventure sport.',
    covers: 'Adaptive sports equipment and recovery costs.',
    amount: 'Varies',
    applyAs: 'individual',
    howToApply: 'Two cycles a year: January 1–31 and July 1–31.',
    sports: [],
  },
  // ── Equipment programs ──────────────────────────────────────────────────
  {
    name: 'NHL/NHLPA Learn to Play',
    url: 'https://learntoplay.nhl.com/',
    category: 'equipment',
    forWho: 'First-time hockey players aged 4–9 (not income-tested).',
    covers: 'A full set of hockey gear plus introductory coaching, run by each NHL club.',
    amount: 'In-kind (full equipment set)',
    applyAs: 'individual',
    howToApply: 'Register through your local NHL team’s Learn to Play page; seasons are set by each club.',
    sports: ['hockey'],
  },
  {
    name: 'Good Sports',
    url: 'https://www.goodsports.org/',
    category: 'equipment',
    forWho: 'US organizations serving ages 3–18 in high-need communities with a participation fee of $299 or less.',
    covers: 'New equipment, footwear and apparel.',
    amount: 'In-kind; the program pays a 10% admin fee on retail value',
    applyAs: 'organization',
    howToApply: 'Your team, school or program applies online.',
    sports: [],
  },
  {
    name: 'Leveling the Playing Field',
    url: 'https://www.levelingtheplayingfield.org/',
    category: 'equipment',
    forWho: 'Youth programs in the Washington DC, Baltimore, Philadelphia, Buffalo, Columbus, Detroit and Atlanta areas.',
    covers: 'Donated and recycled sports equipment.',
    amount: 'In-kind',
    applyAs: 'organization',
    howToApply: 'Your program applies, then picks up gear from their warehouse on a rolling basis.',
    sports: [],
  },
];
